/**
 * AgentChatPanel - Main chat area housing header, messages, and input
 *
 * Central panel of the Travel Agent three-column layout.
 *
 * Layout (CSS Grid — grid-template-rows: auto 1fr auto auto):
 *   Row 1 (auto) — ChatHeader
 *   Row 2 (1fr)  — MessageThread (fills remaining space, scrolls internally)
 *   Row 3 (auto) — TripContextDrawer (always directly above the input)
 *   Row 4 (auto) — InputArea (always pinned to the bottom)
 *
 * CSS Grid is used instead of nested flex so TripContextDrawer and InputArea
 * are guaranteed to be visible at the bottom regardless of conversation length.
 */

import React from 'react';
import { Box } from '@mui/material';
import ChatHeader from './ChatHeader';
import MessageThread from './MessageThread';
import InputArea from './InputArea';
import TripContextDrawer from './TripContextDrawer';
import './AgentChatPanel.css';

/**
 * AgentChatPanel Component
 *
 * @param {Object} props
 * @param {Object|null} props.currentSession - Active session { id, title }
 * @param {Array} props.messages - Messages in the active session
 * @param {boolean} props.isAgentResponding - Whether agent is currently responding
 * @param {string} props.effectiveUserId - Trip owner's user ID
 * @param {string} props.tripId - Trip identifier
 * @param {Object|null} props.currentTrip - Current trip data (passed to TripContextDrawer)
 * @param {Function} props.onSendMessage - Called with (text)
 * @param {Function} props.onNewSession - Start a new session
 * @param {Function} props.onArtifactClick - Artifact chip click handler
 * @param {Function} [props.onReviewTrip] - Called when ✨ Review Trip button is clicked
 * @param {Array}  [props.suggestions=[]] - All outstanding/resolved suggestion docs
 * @param {Function} [props.onApprove] - (suggestionId) => void
 * @param {Function} [props.onDismiss] - (suggestionId) => void
 * @param {Function} [props.onModify] - (suggestionId, note) => void
 * @param {boolean} [props.canApprove=true] - Whether the current user can approve proposals
 */
const AgentChatPanel = ({
  currentSession,
  messages,
  isAgentResponding,
  effectiveUserId,
  tripId,
  currentTrip,
  onSendMessage,
  onNewSession,
  onArtifactClick,
  onReviewTrip,
  suggestions = [],
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const inputDisabled = !tripId || !currentSession;

  const inputArea = (
    <InputArea
      onSendMessage={onSendMessage}
      disabled={inputDisabled}
      isResponding={isAgentResponding}
      placeholder={
        !tripId
          ? 'Select a trip to chat with your Travel Agent'
          : !currentSession
          ? 'Creating session...'
          : 'Ask your travel assistant anything...'
      }
    />
  );

  return (
    <Box className="agent-chat-panel">
      {/* Row 1 (auto): Chat header with session title */}
      <ChatHeader
        sessionTitle={currentSession?.title}
        sessionId={currentSession?.id}
        effectiveUserId={effectiveUserId}
        tripId={tripId}
        onNewSession={onNewSession}
      />

      {/*
        Row 2 (1fr): Scrollable message thread fills all remaining space.
        Empty-state placeholder is rendered inside MessageThread itself.
        CSS Grid guarantees this row gets all the space between ChatHeader and TripContextDrawer.
      */}
      <MessageThread
        messages={messages}
        isAgentResponding={isAgentResponding}
        onArtifactClick={onArtifactClick}
        suggestions={suggestions}
        onApprove={onApprove}
        onDismiss={onDismiss}
        onModify={onModify}
        canApprove={canApprove}
      />

      {/* Row 3 (auto): Trip Context bar with ✨ Review Trip — always directly above the input */}
      <TripContextDrawer
        currentTrip={currentTrip}
        effectiveUserId={effectiveUserId}
        tripId={tripId}
        onReviewTrip={onReviewTrip}
      />

      {/* Row 4 (auto): Message input — always pinned to the bottom */}
      {inputArea}
    </Box>
  );
};

export default AgentChatPanel;
